import React, { useState, useEffect, useRef } from "react";
import api from "../services/api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../App.css";

export default function AdminEmail() {
  const [recipients, setRecipients] = useState([]);
  const [query, setQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("<p>Hello,</p>");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const quillRef = useRef(null);
  const [buttonText, setButtonText] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");

  // Email history / logs
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [resendingFailed, setResendingFailed] = useState(false);

  // ---------------------------
  // Search users as typing
  // ---------------------------
  useEffect(() => {
    if (query.length < 2) {
      setUserResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(
          `/admindash.php?action=search_users&q=${encodeURIComponent(query)}`
        );
        setUserResults(res.data.items || []);
      } catch {
        setUserResults([]);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [query]);

  // ---------------------------
  // Search email templates
  // ---------------------------
  useEffect(() => {
    if (subject.length < 3) {
      setTemplates([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(
          `/admindash.php?action=search_admin_emails&q=${encodeURIComponent(
            subject
          )}`
        );
        setTemplates(res.data.items || []);
      } catch {
        setTemplates([]);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [subject]);

  // ---------------------------
  // Fetch email history on mount
  // ---------------------------
  useEffect(() => {
    fetchHistory();
  }, []);

  async function fetchHistory() {
    setLoadingHistory(true);
    try {
      const res = await api.get("/admindash.php", {
        params: { action: "list_admin_emails" },
      });
      setEmailHistory(res.data.items || []);
    } catch {
      setEmailHistory([]);
    }
    setLoadingHistory(false);
  }

  async function fetchEmailLogs(emailId) {
    setLoadingLogs(true);
    setEmailLogs([]);
    setSelectedEmail(
      emailHistory.find((h) => h.id === emailId) || { id: emailId }
    );
    try {
      const res = await api.get("/admindash.php", {
        params: { action: "get_admin_email_log", email_id: emailId },
      });
      setEmailLogs(res.data.items || []);
    } catch {
      setEmailLogs([]);
    }
    setLoadingLogs(false);
  }

  // ---------------------------
  // Recipients handling
  // ---------------------------
  function addRecipient(u) {
    setRecipients((prev) =>
      prev.some((r) => r.id === u.id) ? prev : [...prev, u]
    );
    setUserResults([]);
    setQuery("");
  }

  function removeRecipient(id) {
    setRecipients((prev) => prev.filter((r) => r.id !== id));
  }

  async function loadGroup(type) {
    try {
      const res = await api.get(
        `/admindash.php?action=get_dynamic_group&type=${type}`
      );
      const newUsers = res.data.items || [];
      setRecipients((prev) => {
        const merged = [...prev];
        for (const u of newUsers) {
          if (!merged.some((m) => m.id === u.id)) merged.push(u);
        }
        return merged;
      });
    } catch {
      alert("Error loading group");
    }
  }

  // ---------------------------
  // Email editor helpers
  // ---------------------------
  const insertMergeTag = (tag) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) {
      setBody((prev) => `${prev}${tag}`);
      return;
    }
    const range = editor.getSelection(true);
    const index = range?.index ?? editor.getLength() - 1;
    editor.insertText(index, tag, "user");
    editor.setSelection(index + tag.length, 0);
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const insertEmailButton = () => {
    const label = buttonText.trim();
    const url = buttonUrl.trim();
    if (!label || !url) {
      alert("Please enter both button text and a button URL.");
      return;
    }
    if (!/^(https?:\/\/|mailto:)/i.test(url)) {
      alert("Button URL must start with https://, http:// or mailto:");
      return;
    }
    const safeLabel = escapeHtml(label);
    const safeUrl = escapeHtml(url);
    const buttonHtml = `<p><br></p><table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:12px 0;"><tr><td bgcolor="#1878e9" style="border-radius:8px;"><a href="${safeUrl}" target="_blank" rel="noopener" style="display:inline-block;padding:12px 18px;background:#1878e9;color:#ffffff;text-decoration:none;border-radius:8px;font-weight:600;font-family:Arial,sans-serif;">${safeLabel}</a></td></tr></table><p><br></p>`;
    setBody((prev) => `${prev}${buttonHtml}`);
    setButtonText("");
    setButtonUrl("");
  };

  // ---------------------------
  // Send email
  // ---------------------------
  async function sendEmail() {
    if (!subject || !body || recipients.length === 0) {
      alert("Please complete all fields and select at least one recipient.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(
        "/admindash.php?action=send_email_custom",
        {
          subject,
          body,
          recipients: recipients.map((r) => r.id),
        },
        {
          timeout: 300000, // 300s just for this long-running request
        }
      );

      const sent = res?.data?.sent ?? 0;
      const total = res?.data?.total_recipients ?? recipients.length;
      const emailId = res?.data?.email_id;

      alert(
        `Email send queued.\nSent: ${sent} of ${total} recipients.${
          emailId ? `\nEmail ID: ${emailId}` : ""
        }`
      );

      setRecipients([]);
      setSubject("");
      setBody("<p>Hello,</p>");

      // refresh history so you can see this send
      fetchHistory();
    } catch (e) {
      console.error(e);
      alert(
        "Error sending email (timeout or server error). Please check Email History for details."
      );
    }
    setLoading(false);
  }

  // ---------------------------
  // Resend failed only
  // ---------------------------
  async function resendFailed(emailId) {
    if (!emailId) return;
    if (
      !window.confirm(
        "Resend this email to failed recipients only?\n\nUsers who already received it successfully will NOT be emailed again."
      )
    ) {
      return;
    }

    setResendingFailed(true);
    try {
      const res = await api.post(
        "/admindash.php?action=resend_failed_admin_email",
        { email_id: emailId },
        { timeout: 300000 }
      );
      const sent = res?.data?.sent ?? 0;
      const total = res?.data?.total ?? 0;
      alert(`Resent to ${sent} of ${total} failed recipients.`);
      await fetchEmailLogs(emailId);
      await fetchHistory();
    } catch (e) {
      console.error(e);
      alert("Error resending to failed recipients. Check server logs.");
    }
    setResendingFailed(false);
  }

  // any failed logs?
  const hasFailed = emailLogs.some((log) => !log.sent);

  return (
    <div
      style={{
        background: "#1e1e1e",
        color: "#f1f1f1",
        minHeight: "100vh",
        padding: 30,
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <h2 style={{ color: "#00b8b8" }}>Send Email</h2>

      {/* Recipient Search */}
      <div style={{ marginTop: 20, position: "relative" }}>
        <label style={{ padding: "5px" }}>To:</label>
        <input
          type="text"
          placeholder="Search users..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="admin-input"
          style={{
            width: "60%",
            padding: "8px",
          }}
        />
        {userResults.length > 0 && (
          <div
            style={{
              background: "#1e1e1e",
              border: "1px solid #333",
              borderRadius: 6,
              marginTop: 5,
              maxHeight: 150,
              overflowY: "auto",
              width: "60%",
              position: "absolute",
              zIndex: 10,
            }}
          >
            {userResults.map((u) => (
              <div
                key={u.id}
                onClick={() => addRecipient(u)}
                style={{
                  padding: 8,
                  cursor: "pointer",
                  color: "#00b8b8",
                }}
              >
                {u.name} ({u.email})
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          {recipients.map((r) => (
            <span
              key={r.id}
              className="admin-pill"
              style={{
                padding: "5px 10px",
                borderRadius: 20,
                marginRight: 5,
                display: "inline-block",
                cursor: "pointer",
              }}
              onClick={() => removeRecipient(r.id)}
            >
              {r.name} ✕
            </span>
          ))}
        </div>

        <div style={{ marginTop: 10 }}>
          <button
            className="admin-pill"
            style={{ cursor: "pointer" }}
            onClick={() => loadGroup("recent_login")}
          >
            Logged in 7 days
          </button>
          <button
            className="admin-pill"
            style={{ cursor: "pointer" }}
            onClick={() => loadGroup("recent_signup")}
          >
            New users (7 days)
          </button>
          <button
            className="admin-pill"
            style={{ cursor: "pointer" }}
            onClick={() => loadGroup("expired_trial")}
          >
            Expired Trials
          </button>
          <button
            className="admin-pill"
            style={{ cursor: "pointer" }}
            onClick={() => loadGroup("expired_subscription")}
          >
            Expired Subs
          </button>
          <button
            className="admin-pill"
            style={{ cursor: "pointer" }}
            onClick={() => loadGroup("verified_email")}
          >
            Verified Emails
          </button>
        </div>
      </div>

      {/* Subject */}
      <div style={{ marginTop: 30, position: "relative" }}>
        <label style={{ padding: "5px" }}>Subject:</label>
        <input
          type="text"
          placeholder="Email subject..."
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="admin-input"
          style={{
            width: "60%",
            padding: "8px",
          }}
        />
        {templates.length > 0 && (
          <div
            style={{
              background: "#1e1e1e",
              border: "1px solid #333",
              borderRadius: 6,
              marginTop: 5,
              maxHeight: 150,
              overflowY: "auto",
              width: "60%",
              position: "absolute",
              zIndex: 10,
            }}
          >
            {templates.map((t) => (
              <div
                key={t.id}
                onClick={() => {
                  setSubject(t.subject);
                  setBody(t.body_html);
                  setTemplates([]);
                }}
                style={{
                  padding: 8,
                  cursor: "pointer",
                  color: "#00b8b8",
                }}
              >
                {t.subject}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ marginTop: 30 }}>
        <label>Body:</label>
        <ReactQuill
          ref={quillRef}
          value={body}
          onChange={setBody}
          theme="snow"
          style={{
            backgroundColor: "#333",
            color: "#fff",
            border: "1px solid #555",
            borderRadius: 6,
            minHeight: "100%",
            height: 280,
            marginBottom: 10,
          }}
        />
        <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>
          You can use personalised merge tags. Click one to insert it at the cursor.
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {["{{id}}","{{name}}","{{email}}","{{acc_type}}","{{acc_type_time}}"].map((tag) => (
            <button key={tag} type="button" onClick={() => insertMergeTag(tag)}
              style={{ background:"#333", color:"#00b8b8", border:"1px solid #444", borderRadius:6, padding:"5px 9px", cursor:"pointer", fontFamily:"monospace", fontSize:12 }}>
              {tag}
            </button>
          ))}
        </div>

        <div style={{ marginTop:18, padding:14, background:"#262626", border:"1px solid #3b3b3b", borderRadius:8, maxWidth:"60%" }}>
          <div style={{ color:"#00b8b8", fontWeight:700, marginBottom:10 }}>Add email button</div>
          <div style={{ display:"grid", gridTemplateColumns:"minmax(160px, .7fr) minmax(280px, 1.3fr) auto", gap:8, alignItems:"end" }}>
            <label style={{ fontSize:12, color:"#ccc" }}>Button text
              <input type="text" value={buttonText} onChange={(e)=>setButtonText(e.target.value)} placeholder="Start my free trial" className="admin-input"
                style={{ width:"100%", boxSizing:"border-box", marginTop:5, padding:8 }} />
            </label>
            <label style={{ fontSize:12, color:"#ccc" }}>Button URL
              <input type="text" value={buttonUrl} onChange={(e)=>setButtonUrl(e.target.value)} placeholder="https://example.com/account/{{id}}" className="admin-input"
                style={{ width:"100%", boxSizing:"border-box", marginTop:5, padding:8 }} />
            </label>
            <button type="button" onClick={insertEmailButton}
              style={{ background:"#1878e9", color:"#fff", border:"none", borderRadius:7, padding:"9px 14px", cursor:"pointer", fontWeight:700, whiteSpace:"nowrap" }}>
              Add Button
            </button>
          </div>
          <div style={{ color:"#888", fontSize:11, marginTop:8 }}>
            Merge tags can be used inside the URL, for example <code style={{ color:"#bbb" }}>{"https://example.com/user/{{id}}"}</code>
          </div>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div
          style={{
            background: "#1e1e1e",
            padding: 20,
            borderRadius: 8,
            marginTop: 20,
            maxWidth: "60%",
          }}
          dangerouslySetInnerHTML={{
            __html:
              `<div style='background:#00b8b8;color:#fff;padding:12px;font-size:18px;text-align:center;'>AAC App</div>` +
              `<div style='padding:20px;font-family:sans-serif;'>${body}</div>` +
              `<div style='font-size:12px;color:#777;padding:12px;text-align:center;'>© ${new Date().getFullYear()} AAC App</div>`,
          }}
        />
      )}

      {/* Actions */}
      <div style={{ marginTop: 30 }}>
        <button
          onClick={() => setPreview(!preview)}
          style={{
            background: "#333",
            color: "#00b8b8",
            border: "none",
            borderRadius: 6,
            padding: "10px 15px",
            marginRight: 10,
          }}
        >
          {preview ? "Hide Preview" : "Preview"}
        </button>

        <button
          onClick={sendEmail}
          disabled={loading}
          className="button-const"
          style={{
            borderRadius: 6,
            padding: "10px 20px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          {loading ? "Sending..." : "Send Email"}
        </button>
      </div>

      {/* Email History */}
      <div
        style={{
          marginTop: 40,
          background: "#262626",
          borderRadius: 10,
          padding: 16,
          maxWidth: 1000,
          border: "1px solid #333",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <h3 style={{ margin: 0, color: "#00b8b8" }}>Email History</h3>
          <button
            onClick={fetchHistory}
            style={{
              background: "#333",
              color: "#ccc",
              border: "none",
              borderRadius: 6,
              padding: "6px 10px",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Refresh
          </button>
        </div>

        {loadingHistory ? (
          <div style={{ padding: 10 }}>Loading history…</div>
        ) : emailHistory.length === 0 ? (
          <div style={{ padding: 10, fontSize: 13 }}>
            No admin emails logged yet.
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    borderBottom: "1px solid #333",
                    padding: "6px",
                    color: "#ccc",
                  }}
                >
                  Subject
                </th>
                <th
                  style={{
                    textAlign: "left",
                    borderBottom: "1px solid #333",
                    padding: "6px",
                    color: "#ccc",
                  }}
                >
                  Created
                </th>
                <th
                  style={{
                    textAlign: "right",
                    borderBottom: "1px solid #333",
                    padding: "6px",
                    color: "#ccc",
                  }}
                >
                  Sent / Total
                </th>
                <th
                  style={{
                    textAlign: "right",
                    borderBottom: "1px solid #333",
                    padding: "6px",
                    color: "#ccc",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {emailHistory.map((h) => (
                <tr key={h.id}>
                  <td
                    style={{
                      borderBottom: "1px solid #222",
                      padding: "6px",
                      maxWidth: 300,
                    }}
                  >
                    {h.subject}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #222",
                      padding: "6px",
                    }}
                  >
                    {h.created_at
                      ? new Date(h.created_at).toLocaleString()
                      : "-"}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #222",
                      padding: "6px",
                      textAlign: "right",
                    }}
                  >
                    {h.sent_count}/{h.total_recipients}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #222",
                      padding: "6px",
                      textAlign: "right",
                    }}
                  >
                    <button
                      onClick={() => fetchEmailLogs(h.id)}
                      style={{
                        background: "#333",
                        color: "#00b8b8",
                        border: "none",
                        borderRadius: 6,
                        padding: "4px 8px",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      View recipients
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Logs for selected email */}
        {selectedEmail && (
          <div
            style={{
              marginTop: 20,
              borderTop: "1px solid #333",
              paddingTop: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <h4 style={{ margin: 0 }}>
                Recipients for:{" "}
                <span style={{ color: "#00b8b8" }}>
                  {selectedEmail.subject || `Email #${selectedEmail.id}`}
                </span>
              </h4>

              {hasFailed && (
                <button
                  onClick={() => resendFailed(selectedEmail.id)}
                  disabled={resendingFailed}
                  style={{
                    background: "#333",
                    color: "#f97373",
                    border: "1px solid #f97373",
                    borderRadius: 6,
                    padding: "6px 10px",
                    cursor: resendingFailed ? "default" : "pointer",
                    fontSize: 12,
                  }}
                >
                  {resendingFailed ? "Resending..." : "Resend failed only"}
                </button>
              )}
            </div>

            {loadingLogs ? (
              <div style={{ padding: 8 }}>Loading recipients…</div>
            ) : emailLogs.length === 0 ? (
              <div style={{ padding: 8, fontSize: 13 }}>
                No log entries found.
              </div>
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  marginTop: 4,
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #333",
                        padding: "6px",
                      }}
                    >
                      User ID
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #333",
                        padding: "6px",
                      }}
                    >
                      Email
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #333",
                        padding: "6px",
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #333",
                        padding: "6px",
                      }}
                    >
                      Sent at
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #333",
                        padding: "6px",
                      }}
                    >
                      Error
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.map((log) => (
                    <tr key={log.id}>
                      <td
                        style={{
                          borderBottom: "1px solid #222",
                          padding: "6px",
                        }}
                      >
                        {log.user_id}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #222",
                          padding: "6px",
                        }}
                      >
                        {log.email}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #222",
                          padding: "6px",
                        }}
                      >
                        {log.sent ? (
                          <span style={{ color: "#4ade80" }}>Sent</span>
                        ) : (
                          <span style={{ color: "#f97373" }}>Failed</span>
                        )}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #222",
                          padding: "6px",
                        }}
                      >
                        {log.sent_at
                          ? new Date(log.sent_at).toLocaleString()
                          : "-"}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #222",
                          padding: "6px",
                        }}
                      >
                        {log.error || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
